import { observer } from 'mobx-react';
import React, { useState } from 'react';

import LockIcon from '@mui/icons-material/Lock';
import { Tooltip, Typography } from '@mui/material';
import { useConfirmation } from 'components/Confirmation';
import store from 'store';

import { checkRole } from '@common/components/RBAC';
import { IReworkingMeetsModel } from '@common/models/Appointments/ReworkingMeets';
import { IReworkingSlotModel } from '@common/models/Appointments/ReworkingSlot';

import MeetModal from './MeetModal';
import AppointmentsBookingModal from './Modal';
import { StyledAppointmentsBookingItemWrap } from './styled';

const MAX_HOURS_BEFORE = 8;

function getStatus(status?: number) {
	const STATUSES: Record<number, any> = {
		0: {
			label: 'Free',
			bg: 'info.main',
			color: 'primary.main',
		},
		1: {
			label: 'Busy',
			bg: 'primary.main',
			color: 'primary.contrastText',
		},
	};
	return (
		STATUSES[status] || {
			label: 'Booked by you',
			bg: 'warning.main',
			color: 'warning.contrastText',
		}
	);
}

const MEET_STATUSES: Record<number, any> = {
	1: {
		label: 'Completed',
		bg: 'success.main',
		color: 'success.contrastText',
	},
	2: {
		label: 'Canceled',
		bg: 'warning.main',
		color: 'warning.contrastText',
	},
	3: {
		label: 'Confirm',
		bg: '#eddfef',
		color: '#a53093',
	},
};

interface PropsType {
	slot: IReworkingSlotModel;
	reworkingMeets: IReworkingMeetsModel;
	teacher: any;
	onSave: (values: any) => void;
	locationMeet: any;
}

function AppointmentsBookingItem({
	slot,
	reworkingMeets,
	teacher,
	onSave,
	locationMeet,
}: PropsType) {
	const isOwnSlot = checkRole('Parent')
		? slot.parent_id === store.profile.parent?.id
		: slot.student_id === store.profile.student?.id;
	const canBook = slot.startDateTime.diffNow('hours').hours > MAX_HOURS_BEFORE
		&& slot.status === 0
		&& slot.location.id === locationMeet.id;
	const canView = slot.status === 1 && isOwnSlot;
	const confirm = useConfirmation();
	const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
	const meet = reworkingMeets.data.find(
		(meetToFind) => meetToFind.meetSlot?.id === slot.id,
	); // @TODO: Optimize
	const slotStatus = isOwnSlot && meet?.status
		? MEET_STATUSES[meet.status]
		: getStatus(!isOwnSlot ? slot.status : null);

	const formattedDate = slot.endDateTime.toFormat('EEE, dd.MM');

	const handleSave = async (values: any) => {
		await onSave(values);
		setIsBookingModalOpen(false);
	};

	const enableClick = (canBook || canView) && !store.auth.isMentorView;

	return (
		<>
			<StyledAppointmentsBookingItemWrap
				{...(enableClick && {
					onClick: () => setIsBookingModalOpen(true),
				})}
				sx={{ backgroundColor: slotStatus.bg, cursor: enableClick && 'pointer' }}
			>
				<Typography variant="subtitle3" sx={{ color: slotStatus.color }}>
					{slot.timesString} {slotStatus.label}
				</Typography>
				{!canBook && slot.status === 0 && (
					<Tooltip
						title={
							slot.location.id !== locationMeet.id
								? 'Slot is free on other location'
								: `Less than ${MAX_HOURS_BEFORE} hours before start`
						}
					>
						<LockIcon sx={{ color: slotStatus.color, fontSize: '1em' }} />
					</Tooltip>
				)}
			</StyledAppointmentsBookingItemWrap>
			{isOwnSlot ? (
				<MeetModal
					open={isBookingModalOpen}
					onClose={() => setIsBookingModalOpen(false)}
					onSubmit={() => {
						confirm()
							.then(async () => {
								await meet.remove();
								slot.setStatus(0, null);
							})
							.finally(() => {
								setIsBookingModalOpen(false);
							});
					}}
					initialValues={{
						program: meet?.programGroup?.program?.id || 0,
						type: meet?.type,
						time: slot.timesString,
						day: formattedDate,
						phone: meet?.phone,
						zoom_access_code: meet?.zoom_access_code,
						zoom_id: meet?.zoom_id,
						zoom_link: meet?.zoom_link,
						format: meet?.format,
						comment_teacher: meet?.comment_teacher,
						comment_student: meet?.comment_student,
					}}
				/>
			) : (
				<AppointmentsBookingModal
					open={isBookingModalOpen}
					onClose={() => setIsBookingModalOpen(false)}
					onSave={handleSave}
					initialValues={{
						program_id: '',
						lesson: '',
						type: 'appointment',
						time: slot.timesString,
						day: formattedDate,
						phone: store.profile.phone,
						zoom_access_code: teacher.zoom_access_code,
						zoom_id: teacher.zoom_id,
						zoom_link: teacher.zoom_link,
						format: 'offline',
						comment_student: '',
					}}
				/>
			)}
		</>
	);
}

export default observer(AppointmentsBookingItem);
